// =============================================================================
// FILE:
// frontend/src/ipe/pms/pages/SiteStoreDashboardPage.jsx
// =============================================================================

import React, {
  useEffect,
  useState
} from "react";

import {
  RefreshCw,
  Filter,
  Download
} from "lucide-react";

import {
  getSiteStoreDashboardSummary,
  getSiteStoreInventoryGrid
} from "../services/siteStoreDashboardService";

import {
  getProjects,
  getProjectPhases,
  getPhaseTasks
} from "../task_material/services/materialReservationService";

import SiteStoreKpiCards
  from "../components/site_store_dashboard/SiteStoreKpiCards";

import SiteStoreFilters
  from "../components/site_store_dashboard/SiteStoreFilters";

import SiteStoreInventoryGrid
  from "../components/site_store_dashboard/SiteStoreInventoryGrid";

import styles
  from "../components/site_store_dashboard/siteStoreDashboardStyles";

import SiteStoreExecutionDrawer
  from "../components/site_store_dashboard/SiteStoreExecutionDrawer";



// =============================================================================
// COMPONENT
// =============================================================================

export default function SiteStoreDashboardPage() {

  // ===========================================================================
  // STATE
  // ===========================================================================

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [summaryData, setSummaryData] =
    useState({});

  const [rows, setRows] =
    useState([]);

  const [projects, setProjects] =
    useState([]);

  const [phases, setPhases] =
    useState([]);

  const [tasks, setTasks] =
    useState([]);

  const [selectedRow, setSelectedRow] =
    useState(null);
  
  const [drawerOpen, setDrawerOpen] =
    useState(false);

  const [pagination, setPagination] =
    useState({

      page: 1,

      page_size: 50,

      total_records: 0
    });


  // ===========================================================================
  // FILTERS
  // ===========================================================================

  const [filters, setFilters] =
    useState({

      search: "",

      stock_status: "",

      project_id: "",

      phase_id: "",

      task_id: "",

      material_id: "",

      category: "",

      from_date: "",

      to_date: ""
    });


  // ===========================================================================
  // HANDLE FILTER CHANGE
  // ===========================================================================

  const handleFilterChange = (
    field,
    value
  ) => {

    setFilters((prev) => ({

      ...prev,

      [field]: value
    }));
  };


  // ===========================================================================
  // LOAD PROJECTS
  // ===========================================================================

  const loadProjects = async () => {

    try {

      const response =
        await getProjects();

      if (response.success) {

        setProjects(
          response.data || []
        );
      }

    } catch (err) {

      console.error(err);
    }
  };


  // ===========================================================================
  // LOAD PHASES
  // ===========================================================================

  const loadPhases = async (
    selectedProjectId
  ) => {

    try {

      if (!selectedProjectId) {

        setPhases([]);

        return;
      }

      const response =
        await getProjectPhases(
          selectedProjectId
        );

      if (response.success) {

        setPhases(
          response.data || []
        );
      }

    } catch (err) {

      console.error(err);
    }
  };


  // ===========================================================================
  // LOAD TASKS
  // ===========================================================================

  const loadTasks = async (
    selectedPhaseId
  ) => {

    try {

      if (!selectedPhaseId) {

        setTasks([]);

        return;
      }

      const response =
        await getPhaseTasks(
          selectedPhaseId
        );

      if (response.success) {

        setTasks(
          response.data || []
        );
      }

    } catch (err) {

      console.error(err);
    }
  };


  // ===========================================================================
  // LOAD DASHBOARD
  // ===========================================================================

  const loadDashboard = async () => {

    try {

      setLoading(true);

      setError("");

      const payload = {

        search:
          filters.search,

        stock_status:
          filters.stock_status
            ? [filters.stock_status]
            : [],

        project_id:
          filters.project_id,

        phase_id:
          filters.phase_id,

        task_id:
          filters.task_id,

        material_id:
          filters.material_id,

        category:
          filters.category,

        from_date:
          filters.from_date,

        to_date:
          filters.to_date,

        page:
          pagination.page,

        page_size:
          pagination.page_size
      };

      const [

        summaryResponse,

        inventoryResponse

      ] = await Promise.all([

        getSiteStoreDashboardSummary(
          payload
        ),

        getSiteStoreInventoryGrid(
          payload
        )
      ]);

      setSummaryData(

        summaryResponse?.data?.data || {}
      );

      setRows(

        inventoryResponse?.data?.data || []
      );

      setPagination((prev) => ({

        ...prev,

        total_records:
          inventoryResponse?.data?.summary
            ?.total_records || 0
      }));

    } catch (err) {

      console.error(err);

      setError(

        err?.response?.data?.message ||

        err.message ||

        "Failed to load dashboard"
      );

    } finally {

      setLoading(false);
    }
  };


  // ===========================================================================
  // INITIAL LOAD
  // ===========================================================================

  useEffect(() => {

    loadProjects();

    loadDashboard();

  }, []);


  // ===========================================================================
  // PROJECT -> PHASE CASCADE
  // ===========================================================================

  useEffect(() => {

    loadPhases(filters.project_id);

    setTasks([]);

    setFilters((prev) => ({

      ...prev,

      phase_id: "",

      task_id: ""
    }));

  }, [filters.project_id]);


  // ===========================================================================
  // PHASE -> TASK CASCADE
  // ===========================================================================

  useEffect(() => {

    loadTasks(filters.phase_id);

    setFilters((prev) => ({

      ...prev,

      task_id: ""
    }));

  }, [filters.phase_id]);


  // ===========================================================================
  // APPLY FILTERS
  // ===========================================================================

  const handleApplyFilters =
    () => {

      loadDashboard();
    };


  // ===========================================================================
  // REFRESH
  // ===========================================================================

  const handleRefresh =
    () => {

      loadDashboard();
    };


  // ===========================================================================
  // EXPORT
  // ===========================================================================

  const handleExport =
    () => {

      console.log(
        "Export Excel"
      );
    };


  // ===========================================================================
  // ROW CLICK
  // ===========================================================================

  const handleRowClick =
  (row) => {

    setSelectedRow(row);

    setDrawerOpen(true);

    console.log(
      "Selected Material Row",
      row
    );
  };
  const handleCloseDrawer =
  () => {

    setDrawerOpen(false);
  };


  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (

    <div style={styles.page}>


      {/* ================================================================ */}
      {/* HEADER */}
      {/* ================================================================ */}

      <div style={styles.headerCard}>


        <div>

          <div style={styles.headerTitle}>

            Site Store Dashboard

          </div>

          <div style={styles.headerSubtitle}>

            Real-time inventory visibility
            and project material execution tracking

          </div>

        </div>


        <div style={styles.headerActions}>


          <button
            style={styles.actionBtn}
            onClick={handleRefresh}
          >

            <RefreshCw size={16} />

            Refresh

          </button>


          <button
            style={styles.actionBtn}
          >

            <Filter size={16} />

            Advanced Filter

          </button>


          <button
            style={styles.exportBtn}
            onClick={handleExport}
          >

            <Download size={16} />

            Export Excel

          </button>

        </div>

      </div>


      {/* ================================================================ */}
      {/* KPI CARDS */}
      {/* ================================================================ */}

      <SiteStoreKpiCards

        summaryData={summaryData}

      />


      {/* ================================================================ */}
      {/* FILTERS */}
      {/* ================================================================ */}

      <SiteStoreFilters

        filters={filters}

        projects={projects}

        phases={phases}

        tasks={tasks}

        onFilterChange={
          handleFilterChange
        }

        onApplyFilters={
          handleApplyFilters
        }

      />


      {/* ================================================================ */}
      {/* ERROR */}
      {/* ================================================================ */}

      {
        error && (

          <div style={styles.errorBox}>

            {error}

          </div>
        )
      }


      {/* ================================================================ */}
      {/* LOADING */}
      {/* ================================================================ */}

      {
        loading && (

          <div style={styles.gridCard}>

            <div style={styles.emptyTd}>

              Loading dashboard...

            </div>

          </div>
        )
      }


      {/* ================================================================ */}
      {/* INVENTORY GRID */}
      {/* ================================================================ */}

      {
        !loading && (

            <>

            <SiteStoreInventoryGrid

                rows={rows}

                pagination={pagination}

                onRowClick={
                handleRowClick
                }

            />

            <SiteStoreExecutionDrawer

                open={drawerOpen}

                row={selectedRow}

                onClose={handleCloseDrawer}

            />

            </>

        )
        }


      {/* ================================================================ */}
      {/* TEMP SELECTED ROW DEBUG */}
      {/* ================================================================ */}

      {
        selectedRow && (

          <div
            style={{
              marginTop: 20,
              padding: 16,
              background: "#ffffff",
              borderRadius: 12,
              boxShadow:
                "0 1px 4px rgba(0,0,0,0.08)"
            }}
          >

            <div
              style={{
                fontWeight: 700,
                marginBottom: 8
              }}
            >

              Selected Material

            </div>

            <div>

              {
                selectedRow.material_name
              }

            </div>

          </div>
        )
      }

    </div>
  );
}